import { computed, toValue, type MaybeRefOrGetter } from 'vue'
import useDelete from '@/core/composables/api/useDelete'
import usePost from '@/core/composables/api/usePost'
import { ApiRequestStatus } from '@/core/models/api/api-request-status'
import type { PortalEnterFailure, PortalEnterResponse } from '../models/portal-session'
import { normalizeAccessCode } from '../utils/access-code'
import { httpStatusCode } from './http-status-code'
import { usePortalToken } from './use-portal-token'

const enterFailure = (error: unknown): PortalEnterFailure => {
  const status = httpStatusCode(error)
  if (status === 404) {
    return 'notFound'
  }
  if (status === 401) {
    return 'unknownCode'
  }
  if (status === 429) {
    return 'rateLimit'
  }
  return 'error'
}

export const usePortalSession = (portalLinkToken: MaybeRefOrGetter<string>) => {
  const { headers, setSessionToken, clearSession, hasSession } = usePortalToken()

  const enterEndpoint = computed(
    () => `portal/${encodeURIComponent(toValue(portalLinkToken))}/enter`,
  )
  const {
    data: entered,
    status: enterStatus,
    error: enterError,
    mutate: postEnter,
  } = usePost<PortalEnterResponse, { accessCode: string }>(enterEndpoint, true)
  
  const { status: endStatus, remove: deleteSession } = useDelete<void>(
    'portal-session',
    true,
    headers,
  )

  const enterWithCode = async (
    rawCode: string,
  ): Promise<{ ok: true; firstName: string } | { ok: false; reason: PortalEnterFailure }> => {
    const accessCode = normalizeAccessCode(rawCode)
    if (!accessCode) {
      return { ok: false, reason: 'empty' }
    }
    if (enterStatus.value === ApiRequestStatus.Loading) {
      return { ok: false, reason: 'error' }
    }
    await postEnter({ accessCode })
    if (enterStatus.value === ApiRequestStatus.Success && entered.value?.portalSessionToken) {
      setSessionToken(entered.value.portalSessionToken)
      return { ok: true, firstName: entered.value.firstName }
    }
    return { ok: false, reason: enterFailure(enterError.value) }
  }

  const endVisit = async () => {
    if (hasSession() && endStatus.value !== ApiRequestStatus.Loading) {
      await deleteSession()
    }
    clearSession()
  }

  return {
    enterStatus,
    endStatus,
    enterWithCode,
    endVisit,
    clearSession,
  }
}
